import { cn } from "@/lib/utils";
import { useDataContext } from "@/app/_context";
import { Webcam } from "lucide-react";

export function TimeGroup() {
  const {
    isHighlightsOnlyMode,
    currentPlaybackTime,
    isPortal,
    playerInstance,
    currentSessionIndex,
    currentPlaylist,
    currentSegment,
  } = useDataContext();

  const highlights = currentPlaylist[currentSessionIndex]?.highlights ?? [];
  const highlightsTotalMs = highlights.reduce((acc, h) => acc + h.length, 0);
  const lastHighlightEndMs = highlights[highlights.length - 1]?.end ?? 0;

  const playbackMs = currentPlaybackTime * 1000;

  // Absolute video time at the current playback position.
  // In highlights-only mode, the player's currentTime is in the highlights-stream
  // timeline, so translate it back to the original video timeline via currentSegment.
  const absoluteCurrentMs =
    isHighlightsOnlyMode && currentSegment
      ? currentSegment.start +
        (playbackMs - currentSegment.highlightsOnlyStart)
      : playbackMs;

  const absoluteTotalMs = isHighlightsOnlyMode
    ? lastHighlightEndMs
    : playerInstance?.state.duration && playerInstance.state.duration !== Infinity
      ? playerInstance.state.duration * 1000
      : 0;

  const showHighlightsRelative = isHighlightsOnlyMode && !isPortal;

  function millisToMinutesAndSeconds(millis: number) {
    const safe = Math.max(0, millis);
    const minutes = Math.floor(safe / 60000);
    const seconds = Number(((safe % 60000) / 1000).toFixed(0));
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  }

  return (
    <div
      className={cn(
        "flex items-center text-sm font-medium",
        isPortal ? "mr-4" : "ml-2.5",
      )}
    >
      <p>{millisToMinutesAndSeconds(absoluteCurrentMs)}</p>
      <div className="mx-1 text-white/80">/</div>
      {absoluteTotalMs > 0 && (
        <p>{millisToMinutesAndSeconds(absoluteTotalMs)}</p>
      )}
      {showHighlightsRelative && (
        <div
          className="ml-3 flex items-center rounded-md bg-accent/10 px-2 py-1 text-accent"
          aria-label="Highlights playlist time"
          title="Highlights playlist time"
        >
          <Webcam size={13} className="mr-1.5 shrink-0" aria-hidden="true" />
          <p>{millisToMinutesAndSeconds(playbackMs)}</p>
          <div className="mx-1 text-accent/80">/</div>
          <p>{millisToMinutesAndSeconds(highlightsTotalMs)}</p>
        </div>
      )}
    </div>
  );
}
